import React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import DashboardMenu from "../../components/dashboardMenu/dashboardMenu";
import Grid from "../../components/grid/grid";
import useApi, { CONTROLLER } from "../../hooks/useApi";
import SingleDatePicker from '../../components/ui/singleDatePicker';
import {
  getBundledMarketAnalysisState,
  resolveBundledMarketAnalysisState,
  shouldRequestMarketAnalysisGeneral,
} from '../../services/homeBundle';
import '../strategies/strategies.css';
import './marketAnalysis.css';

const sentimentTextColor = (score) => {
  if (!Number.isFinite(score)) {
    return undefined;
  }
  const clamped = Math.max(-1, Math.min(1, score));
  const maxAbs = 1;
  const t = Math.min(1, Math.abs(clamped) / maxAbs);
  const lightness = 85 - t * 25;
  const hue = clamped >= 0 ? 120 : 0;
  return `hsl(${hue}, 70%, ${lightness}%)`;
};

const formatSentimentLabel = (label) => {
  const raw = String(label || '').trim().toLowerCase();
  if (raw === 'positive') return 'Positive';
  if (raw === 'negative') return 'Negative';
  return 'Neutral';
};

const buildQueryString = ({ runDate, version }) => {
  const params = [];
  if (runDate) params.push(`runDate=${encodeURIComponent(runDate)}`);
  if (version != null) params.push(`version=${encodeURIComponent(version)}`);
  return params.length ? `?${params.join('&')}` : '';
};

const normalizeStatus = (status) => {
  const raw = String(status || '').trim().toLowerCase();
  if (!raw) return 'Missing';
  if (raw === 'ok') return 'Ready';
  if (raw === 'error') return 'Error';
  if (raw === 'missing') return 'Missing';
  if (raw === 'skipped') return 'Skipped';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const formatRunDateForMessage = (runDate) => {
  const raw = String(runDate || '').trim();
  if (!raw) return '';
  try {
    const parsed = parseISO(raw);
    if (!isValid(parsed)) return raw;
    return format(parsed, 'MMMM d, yyyy');
  } catch (e) {
    return raw;
  }
};

const toFiniteNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const extractAnalysisPayload = (payload) => {
  if (!payload || payload.success === false) {
    return null;
  }
  const data = payload.data || {};
  if (data.analysis && typeof data.analysis === 'object') {
    return data.analysis;
  }
  if (data.subject || data.bullets || data.paragraphs) {
    return data;
  }
  return null;
};

const MARKET_TABLE_MIN_HEIGHT = 220;
const MARKET_TABLE_MAX_HEIGHT = 1400;
const MARKET_TABLE_DEFAULT_HEIGHT = 780;
const MARKET_TABLE_VIEWPORT_BOTTOM_GAP = 26;
const MARKET_PANEL_MIN_WIDTH_PERCENT = 88;
const MARKET_PANEL_DEFAULT_WIDTH_PERCENT = 100;
const MARKET_RESIZE_MODE_Y = 'y';
const MARKET_RESIZE_MODE_X = 'x';
const MARKET_RESIZE_MODE_BOTH = 'both';
const MARKET_RESIZE_CLASS_Y = 'dt-market-resizing-y';
const MARKET_RESIZE_CLASS_X = 'dt-market-resizing-x';
const MARKET_RESIZE_CLASS_BOTH = 'dt-market-resizing-both';
const MARKET_TABLE_PREFERENCE = 'marketAnalysisTable_v2';

const clampMarketTableHeight = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return MARKET_TABLE_DEFAULT_HEIGHT;
  }
  return Math.max(MARKET_TABLE_MIN_HEIGHT, Math.min(MARKET_TABLE_MAX_HEIGHT, Math.round(num)));
};

const clampMarketPanelWidthPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return MARKET_PANEL_DEFAULT_WIDTH_PERCENT;
  }
  return Math.max(MARKET_PANEL_MIN_WIDTH_PERCENT, num);
};

const clearMarketResizeBodyClasses = () => {
  if (typeof document === 'undefined') return;
  document.body.classList.remove(MARKET_RESIZE_CLASS_Y, MARKET_RESIZE_CLASS_X, MARKET_RESIZE_CLASS_BOTH);
};

const setMarketResizeBodyClass = (mode) => {
  if (typeof document === 'undefined') return;
  clearMarketResizeBodyClasses();
  if (mode === MARKET_RESIZE_MODE_X) {
    document.body.classList.add(MARKET_RESIZE_CLASS_X);
    return;
  }
  if (mode === MARKET_RESIZE_MODE_BOTH) {
    document.body.classList.add(MARKET_RESIZE_CLASS_BOTH);
    return;
  }
  document.body.classList.add(MARKET_RESIZE_CLASS_Y);
};

const MarketAnalysis = () => {
  const { execute: executeList, payload: payloadList, loading: loadingList } = useApi(CONTROLLER.MARKET_ANALYSIS_LIST);
  const { execute: executeGeneral, payload: payloadGeneral, loading: loadingGeneral } = useApi(CONTROLLER.MARKET_ANALYSIS_GENERAL);
  const { execute: executeGet, payload: payloadGet, loading: loadingGet } = useApi(CONTROLLER.MARKET_ANALYSIS_GET);
  const bundledGeneralState = React.useMemo(() => getBundledMarketAnalysisState(), []);
  const bundledGeneralSeed = React.useMemo(
    () => resolveBundledMarketAnalysisState(bundledGeneralState, ''),
    [bundledGeneralState],
  );
  const generalRequestStartedRef = React.useRef(false);

  const [runDate, setRunDate] = React.useState('');
  const [version, setVersion] = React.useState(1);
  const [rows, setRows] = React.useState([]);
  const [selectedTicker, setSelectedTicker] = React.useState('');
  const [selectedSummary, setSelectedSummary] = React.useState(null);
  const [generalAnalysis, setGeneralAnalysis] = React.useState(
    () => (['usable', 'fallback'].includes(bundledGeneralSeed?.kind)
      ? bundledGeneralSeed.analysis
      : undefined),
  );
  const [generalContentAsOf, setGeneralContentAsOf] = React.useState(
    () => bundledGeneralSeed?.contentAsOf || '',
  );
  const [generalRunFailed, setGeneralRunFailed] = React.useState(
    () => bundledGeneralSeed?.kind === 'terminal',
  );
  const [analysis, setAnalysis] = React.useState();
  const [tableHeight, setTableHeight] = React.useState(MARKET_TABLE_DEFAULT_HEIGHT);
  const [viewportTableHeight, setViewportTableHeight] = React.useState(MARKET_TABLE_DEFAULT_HEIGHT);
  const [panelWidthPercent, setPanelWidthPercent] = React.useState(MARKET_PANEL_DEFAULT_WIDTH_PERCENT);
  const pageHeadRef = React.useRef(null);
  const tableCardRef = React.useRef(null);
  const topPanelsRef = React.useRef(null);
  const tableResizeStateRef = React.useRef({
    active: false,
    mode: MARKET_RESIZE_MODE_Y,
    startX: 0,
    startY: 0,
    startContainerWidth: 0,
    startHeight: MARKET_TABLE_DEFAULT_HEIGHT,
    startWidthPercent: MARKET_PANEL_DEFAULT_WIDTH_PERCENT,
  });

  React.useEffect(() => {
    if (!payloadList) {
      executeList();
    }
    if (shouldRequestMarketAnalysisGeneral(bundledGeneralState, '') && !payloadGeneral) {
      generalRequestStartedRef.current = true;
      executeGeneral();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!payloadList || !payloadList.data) return;
    const data = payloadList.data || {};
    const listRunDate = data.runDate || data.run_date || '';
    const listVersion = Number(data.version) || 1;
    const items = Array.isArray(data.items) ? data.items : [];

    setRunDate(listRunDate);
    setVersion(listVersion);
    const nextRows = items
      .map((item) => {
        const ticker = String(item?.ticker || '').trim();
        if (!ticker) return null;
        return {
          id: ticker,
          ticker,
          status: item?.status,
          subject: item?.subject,
          sentimentScore: toFiniteNumberOrNull(item?.sentimentScore),
          sentimentLabel: item?.sentimentLabel,
          updatedAt: item?.updatedAt,
        };
      })
      .filter(Boolean);
    setRows(nextRows);
    const resolvedBundledGeneral = resolveBundledMarketAnalysisState(
      bundledGeneralState,
      listRunDate,
    );
    if (['usable', 'fallback'].includes(resolvedBundledGeneral?.kind)) {
      setGeneralAnalysis(resolvedBundledGeneral.analysis);
      setGeneralContentAsOf(resolvedBundledGeneral.contentAsOf || '');
      setGeneralRunFailed(false);
    } else if (resolvedBundledGeneral?.kind === 'terminal') {
      setGeneralAnalysis(null);
      setGeneralRunFailed(true);
    } else if (!generalRequestStartedRef.current) {
      generalRequestStartedRef.current = true;
      setGeneralAnalysis(undefined);
      setGeneralRunFailed(false);
      executeGeneral(
        undefined,
        {
          urlPath: buildQueryString({ runDate: listRunDate, version: listVersion }),
        },
      );
    }
  }, [bundledGeneralState, executeGeneral, payloadList]);

  React.useEffect(() => {
    if (!payloadGeneral) return;
    setGeneralAnalysis(extractAnalysisPayload(payloadGeneral));
    setGeneralContentAsOf(
      payloadGeneral?.data?.contentAsOf ||
      payloadGeneral?.data?.asOfDate ||
      payloadGeneral?.data?.runDate ||
      '',
    );
    const status = String(
      payloadGeneral?.data?.currentStatus || payloadGeneral?.data?.status || '',
    ).trim().toLowerCase();
    setGeneralRunFailed(payloadGeneral.success === false || status === 'error');
  }, [payloadGeneral]);

  React.useEffect(() => {
    if (!payloadGet) return;
    setAnalysis(extractAnalysisPayload(payloadGet));
  }, [payloadGet]);

  const handleRunDateChange = React.useCallback(
    async (nextRunDate) => {
      setRunDate(nextRunDate);
      setSelectedTicker('');
      setSelectedSummary(null);
      setGeneralAnalysis(undefined);
      setGeneralContentAsOf('');
      setGeneralRunFailed(false);
      setAnalysis(undefined);
      generalRequestStartedRef.current = true;
      await Promise.all([
        executeList(
          undefined,
          {
            urlPath: buildQueryString({ runDate: nextRunDate, version }),
          },
        ),
        executeGeneral(
          undefined,
          {
            urlPath: buildQueryString({ runDate: nextRunDate, version }),
          },
        ),
      ]);
    },
    [executeGeneral, executeList, version],
  );

  const columns = React.useMemo(() => ([
    {
      name: 'ticker',
      header: 'Ticker',
      type: 'string',
      hideable: false,
      minWidth: 120,
      textAlign: 'center',
      headerAlign: 'center',
      sortable: true,
    },
    {
      name: 'sentimentScore',
      header: 'Sentiment',
      minWidth: 160,
      type: 'number',
      hideable: false,
      textAlign: 'center',
      headerAlign: 'center',
      sortable: true,
      render: ({ data }) => {
        const score = Number(data?.sentimentScore);
        const label = formatSentimentLabel(data?.sentimentLabel);
        const color = sentimentTextColor(score);
        return (
          <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
            {label}
          </span>
        );
      },
    },
    {
      name: 'subject',
      header: 'Subject',
      type: 'string',
      hideable: false,
      minWidth: 560,
      defaultFlex: 2,
      textAlign: 'start',
      headerAlign: 'center',
      sortable: true,
      render: ({ value }) => (
        <span className="dt-market-subject" title={value || ''}>
          {value || ''}
        </span>
      ),
    },
    {
      name: 'status',
      header: 'Status',
      type: 'string',
      hideable: false,
      minWidth: 140,
      textAlign: 'center',
      headerAlign: 'center',
      sortable: true,
      render: ({ value }) => <span className="dt-market-status">{normalizeStatus(value)}</span>,
    },
  ].map((column) => ({
    ...column,
    sortable: column.sortable !== false,
  }))), []);

  const defaultSortInfo = React.useMemo(() => ([
    { name: 'ticker', dir: 1 },
  ]), []);

  const defaultFilterValue = React.useMemo(
    () => columns.map(({ name, type }) => ({
      name,
      type,
      operator: type === 'number' ? 'inrange' : 'contains',
      value: null,
    })),
    [columns],
  );

  const handleRowClick = React.useCallback(async ({ data }) => {
    const ticker = data?.ticker;
    if (!ticker) return;
    setSelectedTicker(ticker);
    setSelectedSummary({
      subject: data?.subject,
      sentimentScore: data?.sentimentScore,
      sentimentLabel: data?.sentimentLabel,
      status: data?.status,
    });
    setAnalysis(undefined);
    await executeGet(
      undefined,
      {
        urlParams: { ticker },
        urlPath: buildQueryString({ runDate, version }),
      },
    );
  }, [executeGet, runDate, version]);

  const stopTableResize = React.useCallback(() => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    tableResizeStateRef.current.active = false;
    clearMarketResizeBodyClasses();
  }, []);

  const moveTableResize = React.useCallback((event) => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    const { mode } = tableResizeStateRef.current;
    if (mode === MARKET_RESIZE_MODE_Y || mode === MARKET_RESIZE_MODE_BOTH) {
      const deltaY = event.clientY - tableResizeStateRef.current.startY;
      const nextHeight = clampMarketTableHeight(tableResizeStateRef.current.startHeight + deltaY);
      setTableHeight(nextHeight);
    }
    if (mode === MARKET_RESIZE_MODE_X || mode === MARKET_RESIZE_MODE_BOTH) {
      const containerWidth = Number(tableResizeStateRef.current.startContainerWidth);
      if (Number.isFinite(containerWidth) && containerWidth > 0) {
        const deltaX = event.clientX - tableResizeStateRef.current.startX;
        const deltaPct = (deltaX / containerWidth) * 100;
        const nextWidth = clampMarketPanelWidthPercent(tableResizeStateRef.current.startWidthPercent + deltaPct);
        setPanelWidthPercent(nextWidth);
      }
    }
    event.preventDefault();
  }, []);

  const startTableResize = React.useCallback((event, mode) => {
    if (event.button !== 0) {
      return;
    }
    const parentWidth = Number(topPanelsRef.current?.parentElement?.clientWidth);
    const fallbackWidth = Number(topPanelsRef.current?.clientWidth) || Number(tableCardRef.current?.clientWidth);
    const startContainerWidth = Number.isFinite(parentWidth) && parentWidth > 0
      ? parentWidth
      : (Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : 1);
    tableResizeStateRef.current.active = true;
    tableResizeStateRef.current.mode = mode;
    tableResizeStateRef.current.startX = event.clientX;
    tableResizeStateRef.current.startY = event.clientY;
    tableResizeStateRef.current.startContainerWidth = startContainerWidth;
    tableResizeStateRef.current.startHeight = tableHeight;
    tableResizeStateRef.current.startWidthPercent = panelWidthPercent;
    setMarketResizeBodyClass(mode);
    event.preventDefault();
  }, [panelWidthPercent, tableHeight]);

  const startTableResizeY = React.useCallback((event) => {
    startTableResize(event, MARKET_RESIZE_MODE_Y);
  }, [startTableResize]);

  const startTableResizeX = React.useCallback((event) => {
    startTableResize(event, MARKET_RESIZE_MODE_X);
  }, [startTableResize]);

  const startTableResizeBoth = React.useCallback((event) => {
    startTableResize(event, MARKET_RESIZE_MODE_BOTH);
  }, [startTableResize]);

  const refreshViewportTableHeight = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const tableCardEl = tableCardRef.current;
    if (!tableCardEl) {
      return;
    }
    const rect = tableCardEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
    if (!Number.isFinite(rect.top) || !Number.isFinite(viewportHeight) || viewportHeight <= 0) {
      return;
    }
    const availableHeight = viewportHeight - rect.top - MARKET_TABLE_VIEWPORT_BOTTOM_GAP;
    const clampedHeight = clampMarketTableHeight(availableHeight);
    setViewportTableHeight((prev) => (prev === clampedHeight ? prev : clampedHeight));
  }, []);

  React.useLayoutEffect(() => {
    refreshViewportTableHeight();
  }, [
    refreshViewportTableHeight,
    panelWidthPercent,
    generalAnalysis,
    analysis,
    selectedTicker,
    selectedSummary,
    rows.length,
    loadingGeneral,
    loadingGet,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onResize = () => {
      refreshViewportTableHeight();
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [refreshViewportTableHeight]);

  React.useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(refreshViewportTableHeight);
    });
    if (pageHeadRef.current) {
      observer.observe(pageHeadRef.current);
    }
    if (topPanelsRef.current) {
      observer.observe(topPanelsRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [refreshViewportTableHeight]);

  const resetPanelSize = React.useCallback(() => {
    setTableHeight(MARKET_TABLE_DEFAULT_HEIGHT);
    setPanelWidthPercent(MARKET_PANEL_DEFAULT_WIDTH_PERCENT);
  }, []);

  React.useEffect(() => {
    window.addEventListener('mousemove', moveTableResize);
    window.addEventListener('mouseup', stopTableResize);
    return () => {
      window.removeEventListener('mousemove', moveTableResize);
      window.removeEventListener('mouseup', stopTableResize);
      clearMarketResizeBodyClasses();
    };
  }, [moveTableResize, stopTableResize]);

  const detailSubject = String(analysis?.subject || '').trim();
  const detailBullets = Array.isArray(analysis?.bullets) ? analysis.bullets : [];
  const detailParagraphs = Array.isArray(analysis?.paragraphs) ? analysis.paragraphs : [];
  const detailSentimentLabel = formatSentimentLabel(analysis?.sentiment_label || analysis?.sentimentLabel);
  const detailSentimentScore = Number(analysis?.sentiment_score ?? analysis?.sentimentScore);
  const selectedSubject = String(selectedSummary?.subject || '').trim();
  const selectedSentimentLabel = formatSentimentLabel(selectedSummary?.sentimentLabel);
  const selectedSentimentScore = Number(selectedSummary?.sentimentScore);
  const effectiveSentimentLabel = analysis ? detailSentimentLabel : selectedSentimentLabel;
  const effectiveSentimentScore = analysis ? detailSentimentScore : selectedSentimentScore;
  const effectiveSentimentColor = sentimentTextColor(effectiveSentimentScore);
  const runDateMessage = formatRunDateForMessage(runDate) || 'the selected date';
  const generalDateMessage = formatRunDateForMessage(generalContentAsOf) || runDateMessage;
  const detailLoading = Boolean(selectedTicker) && (loadingGet || analysis === undefined);
  const generalSubject = String(generalAnalysis?.subject || '').trim();
  const generalBullets = Array.isArray(generalAnalysis?.bullets) ? generalAnalysis.bullets : [];
  const generalParagraphs = Array.isArray(generalAnalysis?.paragraphs) ? generalAnalysis.paragraphs : [];
  const generalSentimentLabel = formatSentimentLabel(generalAnalysis?.sentiment_label || generalAnalysis?.sentimentLabel);
  const generalSentimentScore = Number(generalAnalysis?.sentiment_score ?? generalAnalysis?.sentimentScore);
  const generalSentimentColor = sentimentTextColor(generalSentimentScore);
  const generalLoading = loadingGeneral || generalAnalysis === undefined;
  const effectiveTableHeight = React.useMemo(() => {
    const preferredHeight = clampMarketTableHeight(tableHeight);
    const maxByViewport = clampMarketTableHeight(viewportTableHeight);
    return Math.min(preferredHeight, maxByViewport);
  }, [tableHeight, viewportTableHeight]);

  return (
    <section className="stockSec dt-stockSec-strategies">
      <div className="container-fluid">
        <DashboardMenu />

        <div className="dt-market-analysis-page">
          <div className="dt-page-head" ref={pageHeadRef} style={{ width: `${panelWidthPercent}%` }}>
            <div className="dt-page-head-left">
              <div className="dt-breadcrumb">Dashboard / Market Analysis</div>
              <h1 className="dt-page-title">Market Analysis</h1>
              <p className="dt-page-subtitle">
                Daily briefs after market close: general market context plus ticker-specific analysis.
              </p>
            </div>
            <div className="dt-page-head-actions">
              <div className="dt-market-asof">
                <span className="dt-market-asof-label">As Of</span>
                <SingleDatePicker
                  value={runDate}
                  onChange={handleRunDateChange}
                  placeholder="Select date"
                  className="dt-market-asof-picker"
                />
              </div>
            </div>
          </div>

          <div className="dt-grid">
            <div
              className="dt-market-top-panels dt-market-resizable-panels"
              ref={topPanelsRef}
              style={{ width: `${panelWidthPercent}%` }}
            >
              <div className="dt-card dt-card-pad dt-market-detail-card">
                <div className="dt-card-header dt-card-header-tight">
                  <div>
                    <h2 className="dt-card-title">General Market Brief</h2>
                    <div className="dt-card-note">
                      Latest available brief as of {generalDateMessage}.
                    </div>
                  </div>
                  {generalAnalysis && (
                    <div className="dt-market-sentiment" style={{ color: generalSentimentColor }}>
                      {generalSentimentLabel}
                      {Number.isFinite(generalSentimentScore) && (
                        <span className="dt-market-sentiment-score">
                          {generalSentimentScore > 0 ? '+' : ''}{generalSentimentScore.toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {generalAnalysis === undefined || generalAnalysis === null ? (
                  <div className="dt-market-empty">
                    <div className="dt-market-empty-text">
                      {generalRunFailed
                        ? `General Market Analysis failed for ${runDateMessage}.`
                        : generalLoading
                        ? 'Loading analysis…'
                        : `No General Market Analysis is available for ${runDateMessage}.`}
                    </div>
                  </div>
                ) : (
                  <div className="dt-market-detail">
                    {generalSubject && (
                      <div className="dt-market-detail-subject">{generalSubject}</div>
                    )}
                    {generalBullets.length > 0 && (
                      <ul className="dt-market-bullets">
                        {generalBullets.map((bullet, idx) => (
                          <li key={`${idx}-${bullet.slice(0, 12)}`}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                    {generalParagraphs.map((paragraph, idx) => (
                      <p key={`${idx}-${paragraph.slice(0, 12)}`} className="dt-market-paragraph">{paragraph}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="dt-card dt-card-pad dt-market-detail-card">
                <div className="dt-card-header dt-card-header-tight">
                  <div>
                    <h2 className="dt-card-title">Ticker Brief</h2>
                    <div className="dt-card-note">
                      {selectedTicker ? `Selected: ${selectedTicker}` : 'Select a ticker from the table below.'}
                    </div>
                  </div>
                  {selectedTicker && (
                    <div className="dt-market-sentiment" style={{ color: effectiveSentimentColor }}>
                      {effectiveSentimentLabel}
                      {Number.isFinite(effectiveSentimentScore) && (
                        <span className="dt-market-sentiment-score">
                          {effectiveSentimentScore > 0 ? '+' : ''}{effectiveSentimentScore.toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {analysis === undefined || analysis === null ? (
                  <div className="dt-market-empty">
                    {selectedTicker && selectedSubject && (
                      <div className="dt-market-empty-subject" title={selectedSubject}>
                        {selectedSubject}
                      </div>
                    )}

                    <div className="dt-market-empty-text">
                      {!selectedTicker
                        ? 'Click a row in the table to view the full analysis here.'
                        : (detailLoading
                          ? 'Loading analysis…'
                          : (selectedSubject
                            ? `A summary is available, but the full Market Analysis brief is not available for ${selectedTicker} on ${runDateMessage}.`
                            : `No Market Analysis is available for ${selectedTicker} on ${runDateMessage}.`))}
                    </div>
                  </div>
                ) : (
                  <div className="dt-market-detail">
                    {(detailSubject || selectedSubject) && (
                      <div className="dt-market-detail-subject">{detailSubject || selectedSubject}</div>
                    )}
                    {detailBullets.length > 0 && (
                      <ul className="dt-market-bullets">
                        {detailBullets.map((bullet, idx) => (
                          <li key={`${idx}-${bullet.slice(0, 12)}`}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                    {detailParagraphs.map((paragraph, idx) => (
                      <p key={`${idx}-${paragraph.slice(0, 12)}`} className="dt-market-paragraph">{paragraph}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className="dt-card dt-card-results dt-market-table-card"
              ref={tableCardRef}
              style={{ width: `${panelWidthPercent}%`, height: `${effectiveTableHeight}px` }}
            >
              <div className="dt-card-pad dt-card-header dt-card-header-tight">
                <div>
                  <h2 className="dt-card-title">All Tickers</h2>
                  <div className="dt-card-note">
                    Click a row to load the full ticker brief.
                    {loadingList ? ' Loading…' : ''}
                  </div>
                  <div className="dt-market-table-tip-row">
                    <div className="dt-market-table-tip">
                      Tip: drag the panel bottom edge, right edge, or corner grip to resize.
                    </div>
                    <button
                      type="button"
                      className="dt-panel-reset-btn"
                      onClick={resetPanelSize}
                      title="Reset panel width and height to default"
                    >
                      Reset Panel Size
                    </button>
                  </div>
                </div>
              </div>
              <div className="dt-market-table">
                <div className="dt-market-table-grid">
                  <Grid
                    preference={MARKET_TABLE_PREFERENCE}
                    idProperty="id"
                    dataSource={rows}
                    columns={columns}
                    className="dt-market-grid"
                    loading={loadingList}
                    sortable
                    defaultSortInfo={defaultSortInfo}
                    enableFiltering
                    defaultFilterValue={defaultFilterValue}
                    enableColumnFilterContextMenu={false}
                    showColumnMenuTool
                    showColumnMenuSortOptions={false}
                    showColumnMenuGroupOptions={false}
                    showColumnMenuFilterOptions={false}
                    showColumnMenuLockOptions={false}
                    onRowClick={handleRowClick}
                    rowHeight={38}
                    headerHeight={48}
                    style={{ height: '100%' }}
                  />
                </div>
                <div
                  className="dt-market-table-resizer-y"
                  onMouseDown={startTableResizeY}
                  title="Drag vertically to resize panel"
                />
                <div
                  className="dt-market-table-resizer-x"
                  onMouseDown={startTableResizeX}
                  title="Drag horizontally to resize panel"
                />
                <div
                  className="dt-market-table-corner-resizer"
                  onMouseDown={startTableResizeBoth}
                  title="Drag to resize panel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketAnalysis;
